import { Info } from "lucide-react";

import { type Conversation } from "shared/apiClient";
import { getText, titleCase } from "shared/lib";

import { useGetObjectsNearAddress } from "shared/apiHooks";

import {
  MapComponent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components";
import { lotSizeDescription } from "@/lib/utils.ts";

export interface LotProps {
  conversation: Conversation | undefined | null;
  printable?: boolean;
}

export function Lot({ conversation, printable }: LotProps) {
  const { data: nearObjectsResponse } = useGetObjectsNearAddress({
    path: {
      address_id: conversation?.address_data?.id || "",
    },
  });

  const parseOverlays = () => {
    const overlaysData = conversation?.address_data?.overlays;
    if (!overlaysData) return [];
    if (typeof overlaysData === "string") {
      try {
        return JSON.parse(overlaysData);
      } catch {
        return [];
      }
    }
    return Array.isArray(overlaysData) ? overlaysData : [];
  };

  const overlays = parseOverlays();
  const filteredOverlays = overlays.filter(
    (overlay) => overlay.group && overlay.group !== "",
  );
  const filteredOtherControls = overlays.filter(
    (overlay) => !overlay.group || overlay.group === "",
  );

  const renderOverlays = () => {
    return (
      <ul className="text-bodymd list-outside list-disc pl-6">
        {filteredOverlays.map((overlay, index) => {
          return (
            <li key={index + "lot-overlay"}>
              {titleCase(overlay.description || "")}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderOtherControls = () => {
    return (
      <ul className="text-bodymd list-outside list-disc pl-6">
        {filteredOtherControls.map((overlay, index) => {
          return <li key={index + "other-control"}>{overlay.description}</li>;
        })}
      </ul>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-5">
        <h2 className="text-headinglg text-textPrimary">{getText("Lot")}</h2>

        {printable ? null : (
          <MapComponent
            latitude={conversation?.address_data?.gps_latitude ?? undefined}
            longitude={conversation?.address_data?.gps_longitude ?? undefined}
            addressBoundary={conversation?.address_data?.boundary ?? undefined}
            nearObjects={nearObjectsResponse?.data.near_objects ?? undefined}
            className="h-[340px] max-h-[340px] min-h-[200px] w-full rounded bg-[#E3E2E1]"
          />
        )}

        {conversation?.address_data?.unstructured_address ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Address")}</h3>

            <p className="text-bodymd capitalize">
              {titleCase(conversation.address_data.unstructured_address)}
            </p>
          </div>
        ) : null}

        {conversation?.address_data?.lga_name ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Council")}</h3>

            <p className="text-bodymd capitalize">
              {conversation.address_data.lga_name.toLowerCase()}
            </p>
          </div>
        ) : null}

        {conversation?.address_data?.lot_size_sqm ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Size")}</h3>

            <p className="text-bodymd">
              <p className="text-bodymd">
                {lotSizeDescription(conversation?.address_data)}
              </p>
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Zone")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent>
                {getText(
                  "Zones regulate the use and development of land. Different zones typically represent different types of land use, such as residential or commercial.",
                )}
              </TooltipContent>
            </Tooltip>
          </div>

          <p className="text-bodymd">
            {conversation?.address_data?.zones[0]?.description
              ? titleCase(conversation?.address_data?.zones[0]?.description)
              : getText("🚧 No zone for this address was found.")}
          </p>
        </div>

        {filteredOverlays.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <h3 className="text-headingsm">{getText("Overlays")}</h3>

              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-3 w-3" />
                </TooltipTrigger>
                <TooltipContent className="flex flex-col gap-1">
                  <p>
                    {getText(
                      "An overlay is a layer of additional planning controls that is applied to a specific area.",
                    )}
                  </p>
                  <p>
                    {getText(
                      "Overlays are used to address specific planning issues or to protect certain features or values in an area.",
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {renderOverlays()}
          </div>
        )}

        {filteredOtherControls.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <h3 className="text-headingsm">
                {getText("Other controls or instruments")}
              </h3>

              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-3 w-3" />
                </TooltipTrigger>
                <TooltipContent className="flex flex-col gap-1">
                  <p>
                    {getText(
                      "Other controls or instruments are additional planning regulations that may apply to your property.",
                    )}
                  </p>
                  <p>
                    {getText(
                      "These can include heritage listings, environmental protections, or other special planning considerations.",
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {renderOtherControls()}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
